import { Tabs } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ConversationAPI } from 'api/conversations';
import { DocumentId, ProjectId } from 'api/models';
import { ConversationPane } from 'components/organisms/conversations';
import {
  updateActiveTab,
  useDocuments,
} from 'components/organisms/document/document-store';
import {
  AbsolutePrivatePlacard,
  PrivatePlacard,
  PrivatePlacardHolder,
} from 'components/organisms/spaces/space-tab/styles';
import { useApprovalsPaneContext } from 'contexts/approvals-pane';
import {
  DocumentTabsFreeMode,
  selectDocumentTabsFreeMode,
  toggleDocumentTabsFreeMode,
} from 'data/layout';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useGetConversationWithWS } from 'hooks/use-conversations';
import { useQueryParams } from 'hooks/use-query-params';
import { useToggleFreeMode } from 'hooks/use-toggle-free-mode';
import { useUser } from 'hooks/use-user';
import { useDocumentApprovalsQuery } from 'queries/document-approvals';
import { scrollToCommentInDoc } from '../../doc-utils';
import { TabNames } from '../../document.types';
import { ContentCommentIdMap } from '../../services/comment';
import { DocumentDiscussions } from '../doc-discussions/doc-discussions';
import { DocumentTextTasks } from '../doc-tasks';
import { useDocTasks } from '../document-tasks-hook';
import { MemoizedDocumentApprovals } from './document-approvals';
import {
  ApprovalsTabLabel,
  ConversationsTabLabel,
  DiscussionsTabLabel,
  TasksTabLabel,
} from './tab-labels';

const { TabPane } = Tabs;

export const StyledTabs = styled(Tabs)`
  border-left: 1px solid var(--coolest-gray-20);
  .ant-tabs-tab {
    margin: 0;
    padding: var(--spacing-03) var(--spacing-04);
    border-bottom: none;
    min-width: var(--tabs-width);
    margin-bottom: var(--spacing-01);
    height: var(--tabs-height);
  }

  .ant-tabs-tab-active {
    pointer-events: auto;
  }
`;

export const StyledTabPane = styled(TabPane)`
  height: calc(128px + var(--document-tab-pane-height));
  overflow: hidden;
  position: relative;
`;

export const DocumentTabContainer = styled.div<{ $hidePaneContent: boolean }>`
  width: ${({ $hidePaneContent }) =>
    $hidePaneContent ? 'var(--tabs-width)' : 'var(--pane-width)'};
  transition: all 0.3s cubic-bezier(0.29, 0.04, 0.23, 1);

  ${StyledTabs} {
    .ant-tabs-content-holder {
      width: calc(var(--pane-width) - var(--tabs-width));
      margin-right: var(--tabs-width);
      border: none;
    }
    .ant-tabs-nav {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      background: var(--white-100);
      border-left: 1px solid var(--coolest-gray-20);
      border-top: 1px solid var(--coolest-gray-20);
    }

    .ant-tabs-tabpane {
      padding-right: 0;
    }
  }
`;

interface Props {
  projectId: number;
  documentId: number;
  docTitle?: string;
  isPrivate: boolean;
  canShowComments: boolean;
  commentThreadId: string;
  editorCommentMap: ContentCommentIdMap;
  newCommentBaseContent: string;
  showDiscussionForm: boolean;
  onDiscussionFormClose: (val: boolean) => void;
}
export const DocumentTabs: FC<Props> = ({
  projectId,
  documentId,
  docTitle,
  isPrivate,
  canShowComments,
  commentThreadId,
  editorCommentMap,
  newCommentBaseContent,
  showDiscussionForm,
  onDiscussionFormClose,
}) => {
  const [state, documentStoreDispatch] = useDocuments();
  const queryParams = useQueryParams();
  const tabParams = queryParams.get('tab');

  useEffect(() => {
    if (tabParams === 'approvals') {
      updateActiveTab(documentStoreDispatch, TabNames.Approvals);
    }
  }, [tabParams, documentStoreDispatch]);

  const tasksFromStore = useDocTasks(projectId, documentId);
  const generalTasks = tasksFromStore.filter((task) => {
    return task.source?.metaData
      ? !task.source?.metaData.resourceSectionId
      : true;
  });
  const currentUser = useUser();
  const [discussionLength, setDiscussionLength] = useState(0);
  const { newApprovalData, setNewApprovalData } = useApprovalsPaneContext();
  const { data: approvals } = useDocumentApprovalsQuery(
    DocumentId(documentId),
    ProjectId(projectId),
  );
  const canShowApprovalsPane =
    Boolean(newApprovalData) || Boolean(approvals?.length);

  const dispatch = useDispatch();
  const documentsTabsActive = useSelector(selectDocumentTabsFreeMode);
  const onToggle = (value: boolean) => {
    dispatch(toggleDocumentTabsFreeMode(value));
  };

  const documentTabsFreeHandler = useToggleFreeMode({
    active: documentsTabsActive,
    localStorageKey: DocumentTabsFreeMode,
    onToggle,
  });

  const { data } = useGetConversationWithWS(projectId, {
    type: 'DOCUMENT',
    id: documentId,
  });

  const comments = useMemo(() => data?.comments ?? [], [data]);

  function onTabClickHandler(tabClicked) {
    if (!documentTabsFreeHandler.active && tabClicked === state.activeTab) {
      documentTabsFreeHandler.enter();
    } else {
      documentTabsFreeHandler.exit();
    }
  }

  const onApprovalCancel = useCallback(() => {
    setNewApprovalData(undefined);
    if (approvals?.length) {
      updateActiveTab(documentStoreDispatch, TabNames.ConversationsTab);
    }
    documentTabsFreeHandler.enter();
  }, [
    approvals?.length,
    documentStoreDispatch,
    documentTabsFreeHandler,
    setNewApprovalData,
  ]);

  const onApprovalSubmit = useCallback(() => {
    setNewApprovalData(undefined);
  }, [setNewApprovalData]);
  const projectResource = useProjectAsRBACResource(projectId);
  const { hasAccessToResource } = useWithAccess();
  // const hasApprovalReadAccess = hasAccessToResource({
  //   rule: 'DOCUMENT_APPROVAL:READ',
  //   resourceData: projectResource,
  // });
  const hasCommentReadAccess = hasAccessToResource({
    // rule: 'DOCUMENT_COMMENT:READ',
    rule: 'COMMENT:READ',
    resourceData: projectResource,
  });
  const hasTaskReadAccess = hasAccessToResource({
    // rule: 'DOCUMENT_TASK:READ',
    rule: 'TASK:READ',
    resourceData: projectResource,
  });
  const showPane =
    (approvals && canShowApprovalsPane) ||
    hasCommentReadAccess ||
    hasTaskReadAccess;

  return (
    <DocumentTabContainer
      className="document-tab-container"
      $hidePaneContent={documentTabsFreeHandler.active || !showPane}
    >
      <StyledTabs
        activeKey={state.activeTab}
        onChange={(tab) => {
          updateActiveTab(documentStoreDispatch, tab as TabNames);
        }}
        tabPosition="right"
        tabBarStyle={{
          minWidth: 'var(--tabs-width)',
          width: 'var(--tabs-width)',
          zIndex: 10,
        }}
        onTabClick={onTabClickHandler}
      >
        {hasCommentReadAccess ? (
          <StyledTabPane
            tab={<ConversationsTabLabel count={comments.length} />}
            key={TabNames.ConversationsTab}
          >
            <PrivatePlacardHolder>
              {isPrivate && (
                <AbsolutePrivatePlacard data-cy="private.conversation.info">
                  <FormattedMessage id="document.private.conversations" />
                </AbsolutePrivatePlacard>
              )}
            </PrivatePlacardHolder>

            <ConversationPane
              onStarMessage={() => {}}
              spotlights={[]}
              context={{ type: 'DOCUMENT', id: documentId }}
              projectId={projectId}
              accountId={currentUser.account.accountId}
              apiService={ConversationAPI}
            />
          </StyledTabPane>
        ) : null}
        {canShowComments && hasCommentReadAccess && (
          <StyledTabPane
            forceRender
            tab={<DiscussionsTabLabel count={discussionLength} />}
            key={TabNames.CommentsTab}
          >
            {isPrivate && (
              <AbsolutePrivatePlacard>
                <FormattedMessage id="document.private.comments" />
              </AbsolutePrivatePlacard>
            )}
            {state.editor ? (
              <DocumentDiscussions
                onActiveDiscussionLengthChange={setDiscussionLength}
                docId={documentId}
                projectId={projectId}
                onDiscussionClick={scrollToCommentInDoc}
                onCloseForm={() => {
                  onDiscussionFormClose(false);
                }}
                editorCommentMap={editorCommentMap}
                showForm={showDiscussionForm}
                editor={state.editor}
                commentThreadId={commentThreadId}
                newCommentBaseContent={newCommentBaseContent}
              />
            ) : null}
          </StyledTabPane>
        )}
        {hasTaskReadAccess ? (
          <StyledTabPane
            tab={<TasksTabLabel count={generalTasks.length} />}
            key={TabNames.Tasks}
          >
            {isPrivate && (
              <PrivatePlacard>
                <FormattedMessage id="document.private.tasks" />
              </PrivatePlacard>
            )}
            <DocumentTextTasks
              documentId={documentId}
              projectId={projectId}
              docTitle={docTitle}
            />
          </StyledTabPane>
        ) : null}
        {canShowApprovalsPane && approvals && (
          <StyledTabPane
            tab={<ApprovalsTabLabel count={approvals?.length || 0} />}
            key={TabNames.Approvals}
          >
            <MemoizedDocumentApprovals
              isPrivate={isPrivate}
              onNewApprovalCancel={onApprovalCancel}
              onNewApprovalSubmit={onApprovalSubmit}
              newData={newApprovalData}
              data={approvals}
              documentId={documentId}
              projectId={projectId}
            />
          </StyledTabPane>
        )}
      </StyledTabs>
    </DocumentTabContainer>
  );
};

export const MemoizedDocTabs = React.memo(DocumentTabs);
